import React, { useEffect, useRef, useState } from 'react';
import './PlayerPortrait.css';

export function localOfficialPortrait(url) {
  if (!url) return url;
  try {
    const source = new URL(url, window.location.origin);
    const sport = source.hostname === 'ibis.innebandy.se' ? 'floorball'
      : source.hostname === 'information-20ca4.kxcdn.com' ? 'football' : null;
    const prefix = sport === 'floorball' ? '/images/Spelare/' : '/player/';
    const filename = source.pathname.slice(prefix.length);
    const query = [...source.searchParams.entries()];
    const validQuery = !query.length || (sport === 'football' && query.length === 1 && query[0][0] === 't' && /^[A-Za-z0-9_.-]{1,100}$/.test(query[0][1]));
    if (sport && source.protocol === 'https:' && !source.port && validQuery && !source.hash && source.pathname.startsWith(prefix) && filename && !filename.includes('..') && /^[A-Za-z0-9_./%-]+$/.test(filename)) {
      return `/sportsim/api/player-portraits/official/${sport}/${filename}${source.search}`;
    }
    if (source.origin === window.location.origin) return url;
  } catch {}
  return null;
}

export function TemporaryPortraitMark() {
  return <span className="portrait-mark" role="img" aria-label="Tillfällig bild – inte officiell">
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="m16 3 5 5L8 21H3v-5L16 3Z"/><path d="m14 5 5 5"/>
    </svg>
  </span>;
}

export function hockeyProfilePath(player) {
  if (player.squad_profile_url) return player.squad_profile_url;
  if (!player.simulation || !player.match_id) return null;
  return '/sportsim/api/hockey/player-profile?' + new URLSearchParams({
    simulation: player.simulation, match_id: player.match_id, side: player.side,
    name: player.name, number: String(player.number || '')
  });
}

export function usePlayerPortrait(profilePath, officialUrl, ready) {
  const [portrait, setPortrait] = useState(null);
  const [allowed, setAllowed] = useState(false);
  useEffect(() => {
    const controller = new AbortController();
    fetch('/admin/check', {signal: controller.signal, cache: 'no-store'})
      .then(response => setAllowed(response.ok)).catch(() => {});
    return () => controller.abort();
  }, []);
  useEffect(() => {
    setPortrait(null);
    if (!profilePath || !ready) return undefined;
    const controller = new AbortController();
    fetch('/sportsim/api/player-portraits?' + new URLSearchParams({profile_path: profilePath}),
      {signal: controller.signal, cache: 'no-store'})
      .then(response => response.ok ? response.json() : null)
      .then(data => { if (!controller.signal.aborted) {
        setPortrait(data); window.dispatchEvent(new Event('sports-hq-portrait-changed'));
      }}).catch(() => {});
    return () => controller.abort();
  }, [profilePath, officialUrl, ready]);
  return {imageUrl: localOfficialPortrait(portrait?.image_url || officialUrl), overridden: portrait?.overridden, controls: allowed && portrait &&
    <PortraitEditor profilePath={profilePath} portrait={portrait} onChange={value => { setPortrait(value); window.dispatchEvent(new Event('sports-hq-portrait-changed')); }}/>};
}

function PortraitEditor({profilePath, portrait, onChange}) {
  const input = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [preparing, setPreparing] = useState(false);
  const [error, setError] = useState('');
  const preparation = useRef(null);
  useEffect(() => () => preparation.current?.abort(), []);
  useEffect(() => {
    if (!file) { setPreview(null); return undefined; }
    const url = URL.createObjectURL(file); setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);
  const choose = async event => {
    const selected = event.target.files?.[0]; event.target.value = '';
    if (!selected) return;
    preparation.current?.abort();
    setFile(null); setError('');
    if (selected.size > 50 * 1024 * 1024) { setError('Bilden får vara högst 50 MB.'); return; }
    const controller = new AbortController(); preparation.current = controller;
    setPreparing(true);
    try {
      const form = new FormData(); form.append('file', selected);
      const response = await fetch('/sportsim/api/admin/player-portraits/preview', {
        method: 'POST', body: form, signal: controller.signal
      });
      if (!response.ok) {
        const result = await response.json().catch(() => ({}));
        throw new Error(typeof result.detail === 'string' ? result.detail : 'Bilden kunde inte förberedas. Försök igen.');
      }
      const converted = await response.blob();
      if (!controller.signal.aborted) setFile(new File([converted], 'spelarbild.jpg', {type: 'image/jpeg'}));
    } catch (failure) {
      if (!controller.signal.aborted) setError(failure.message || 'Bilden kunde inte förberedas. Försök igen.');
    } finally {
      if (!controller.signal.aborted) setPreparing(false);
    }
  };
  const save = async remove => {
    setBusy(true); setError('');
    try {
      const form = new FormData();
      form.append('profile_path', profilePath); form.append('official_url', portrait.official_url);
      if (!remove) {
        const prepared = await fetch('/sportsim/api/admin/player-portraits?' + new URLSearchParams({profile_path: profilePath}), {cache: 'no-store'});
        const baseline = await prepared.json();
        if (!prepared.ok) throw new Error(typeof baseline.detail === 'string' ? baseline.detail : 'Den officiella bilden kunde inte kontrolleras.');
        if (baseline.official_url !== portrait.official_url || baseline.revision !== portrait.revision) throw new Error('Bilden har ändrats. Öppna spelarprofilen igen.');
        form.append('official_fingerprint', baseline.official_sha);
        form.append('revision', baseline.revision);
      }
      if (file) form.append('file', file);
      const url = '/sportsim/api/admin/player-portraits' + (remove ? '?' + new URLSearchParams({profile_path: profilePath, revision: portrait.revision}) : '');
      const response = await fetch(url, {method: remove ? 'DELETE' : 'POST', ...(remove ? {} : {body: form})});
      const result = await response.json();
      if (!response.ok) throw new Error(typeof result.detail === 'string' ? result.detail : 'Bilden kunde inte sparas. Försök igen.');
      onChange(result); setFile(null);
    } catch (failure) { setError(failure.message || 'Bilden kunde inte sparas. Försök igen.'); }
    finally { setBusy(false); }
  };
  return <div className="portrait-editor">
    <input ref={input} hidden type="file" accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif" aria-label="Välj tillfällig spelarbild" onChange={choose}/>
    {preview ? <>
      <img className="portrait-editor__preview" src={preview} alt="Förhandsvisning av tillfällig bild"/>
      <button type="button" disabled={busy} onClick={() => save(false)}>{busy ? 'Sparar…' : 'Spara tillfällig bild'}</button>
      <button type="button" disabled={busy} onClick={() => setFile(null)}>Avbryt</button>
      <small>Bilden visas tills den officiella bilden ändras.</small>
    </> : <>
      <button type="button" disabled={busy || preparing} onClick={() => input.current.click()}>Byt bild</button>
      {portrait.overridden && <button type="button" disabled={busy || preparing} onClick={() => save(true)}>Återställ officiell bild</button>}
    </>}
    {preparing && <p role="status">Förbereder bilden…</p>}
    {error && <p role="alert">{error}</p>}
  </div>;
}
